<script setup lang="ts">
const { getUserProfile, getUserCollection } = useUserController();
const { trackEvent } = useGAnalytics();
const appStateStore = useAppStateStore();
const merchant = ref<string>(appStateStore.currentMerchant);
const userId = useRoute().params.id as string;
const route = useRoute();
const router = useRouter();
const title = ref<string>("");
const filterParams = computed(() => (route.query.filter as string) || "");
const isLoadingMore = ref<boolean>(false);
const isServerDown = ref<boolean>(false);
const page = ref<number>(1);
const pageSize = ref<number>(12);

useSeoMeta({
  title,
});

const setEmptyUserProfile = () => {
  return {
    id: "",
    username: "",
    displayName: "",
    tagline: "",
    profilePicture: "",
    profileBanner: "",
    registrationTime: "",
    roles: [],
  };
};

const { data: userData, status } = useAsyncData(
  `getPublicUser/${userId}`,
  async () => {
    try {
      const [userProfile, collection] = await Promise.all([
        getUserProfile(userId),
        getUserCollection(
          merchant.value,
          userId,
          page.value,
          pageSize.value,
          filterParams.value
        ),
      ]);
      if (userProfile.statusCode !== 200) router.push("/");
      title.value = userProfile.payload?.username || "";
      return {
        userProfile: userProfile.payload || setEmptyUserProfile(),
        collection,
      };
    } catch (error) {
      isServerDown.value = true;
      return {
        userProfile: setEmptyUserProfile(),
        collection: {
          records: [],
          total: 0,
          page: 0,
          totalPages: 0,
        },
      };
    }
  },
  {
    default() {
      return {
        userProfile: setEmptyUserProfile(),
        collection: {
          records: [],
          total: 0,
          page: 0,
          totalPages: 0,
        },
      };
    },
    watch: [() => merchant.value, filterParams],
    server: false,
    lazy: true,
  }
);

const updateMerchant = (newMerchant: string) => {
  merchant.value = newMerchant;
};

useInfiniteScroll(
  window,
  async () => {
    isLoadingMore.value = true;
    page.value++;
    const response = await getUserCollection(
      merchant.value,
      userId,
      page.value,
      pageSize.value,
      filterParams.value
    );
    if (!response) {
      return;
    }
    userData.value.collection.records = [
      ...userData.value.collection.records,
      ...response.records,
    ];
    userData.value.collection.page = response.page;
    userData.value.collection.totalPages = response.totalPages;
    isLoadingMore.value = false;
  },
  {
    distance: 100,
    canLoadMore: () =>
      !isLoadingMore.value &&
      userData.value.collection.total >
        userData.value.collection.records.length &&
      userData.value.collection.page < userData.value.collection.totalPages,
  }
);
</script>

<template>
  <LoaderCommonHero v-if="status === 'pending' || status === 'idle'" />
  <CommonHeroPublic v-else :user-profile="userData.userProfile" :id="userId" />
  <LoaderCardGridSkeleton
    v-if="status !== 'success' || !appStateStore.areTidsLoaded" />
  <template v-else>
    <BaseLayoutItemGrid>
      <template #components>
        <Toolbar
          :item-number="userData.collection.total"
          :is-collection-view="true"
          :show-filters="true"
          @update-merchant="updateMerchant" />
        <CommonNotFoundItems
          v-if="userData.collection.total < 1"
          :msg="$t('main.userCollection.noItemsInCollection')" />
        <CommonNotFoundServer v-if="isServerDown" />
      </template>
      <template v-if="!isServerDown" #list-items>
        <template
          v-for="collectible in userData.collection.records"
          :key="collectible.id">
          <NuxtLinkLocale
            :to="`/collectible/${collectible.id}`"
            :data-test="`link-public-profile-collectible-${collectible.id}`"
            @click="
              trackEvent(`click_collectible_${$route.name as string}`, {
                collectibleId: collectible.id,
              })
            ">
            <CardCollection
              :collectible="collectible"
              :show-recommended-price="false" />
          </NuxtLinkLocale>
        </template>
      </template>
    </BaseLayoutItemGrid>
  </template>
  <div v-if="isLoadingMore" class="py-10 flex flex-col items-center gap-4">
    <p class="text-neutral-300 text-center">
      {{ $t("main.storeMain.loadingMore") }}
    </p>
    <LoaderSpinner size="2xl" />
  </div>
</template>
